import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress,
  Chip,
  Box,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useToaster } from 'src/utils/toaster/toasterContext';
import DMTemplateConfiguration from '../create-automation-trigger/DMTemplateConfiguration';
import HttpServices from 'src/services/httpService';
import { postsModuleURL } from 'src/services/urlService';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';

const EditTriggerDialog = ({ open, onClose, trigger, type, onSave }) => {
  const { showToast } = useToaster();
  const { t: translate } = useTranslation();
  const [formData, setFormData] = useState({
    id: 0,
    webhook_profile_id: 0,
    post_id: '',
    trigger_type: '',
    trigger_values: [],
    any_trigger_value: false,
    dm_message: '',
    comment_reply: '',
    dm_template: null,
    is_dm_template: false,
    allPost: false,
  });
  const [allPost, setAllPost] = useState(false);
  const [inputTriggerValue, setInputTriggerValue] = useState('');
  const [loadingPostId, setLoadingPostId] = useState(null);

  useEffect(() => {
    if (trigger) {
      setFormData({
        id: trigger.id || 0,
        webhook_profile_id: trigger.webhook_profile_id || 0,
        post_id: trigger.post_id || '',
        trigger_type: trigger.trigger_type || '',
        trigger_values: trigger.trigger_values || (trigger.trigger_value ? trigger.trigger_value.split(',') : []),
        any_trigger_value: trigger.any_trigger_value || false,
        dm_message: trigger.dm_message || '',
        comment_reply: trigger.comment_reply || '',
        dm_template: trigger.dm_template || null,
        is_dm_template: trigger.is_dm_template || false,
        allPost: trigger.allPost || false,
      });
      setAllPost(trigger.allPost || false);
    }
  }, [trigger]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDmTemplateToggle = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      is_dm_template: checked,
      dm_message: checked ? '' : prev.dm_message,
      dm_template: checked
        ? prev.dm_template || {
            title: '',
            subtitle: '',
            image_url: '',
            default_action: { type: 'web_url', url: '' },
            buttons: [],
          }
        : null,
    }));
  };

  const handleAnyTriggerValueToggle = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      any_trigger_value: checked,
      trigger_values: checked ? [] : prev.trigger_values,
    }));
  };

  const handleAddTriggerValue = () => {
    if (inputTriggerValue.trim()) {
      setFormData((prev) => ({
        ...prev,
        trigger_values: [...prev.trigger_values, inputTriggerValue.trim()],
      }));
      setInputTriggerValue('');
    }
  };

  const handleDeleteTriggerValue = (valueToDelete) => {
    setFormData((prev) => ({
      ...prev,
      trigger_values: prev.trigger_values.filter((value) => value !== valueToDelete),
    }));
  };

  const handleTemplateChange = (newTemplate) => {
    setFormData((prev) => ({ ...prev, dm_template: newTemplate }));
  };

  const handlePostClick = (postId, Id) => {
    setLoadingPostId(Id);
    let url = '';
    if (trigger.connected_profile_details.social_media === 'facebook') {
      url = `${postsModuleURL.getFacebookPostDetails}${trigger.connected_profile_details.connected_profile_id}&post_id=${postId}`;
    } else {
      url = `${postsModuleURL.getIntagramPostDetails}${trigger.connected_profile_details.connected_profile_id}&post_id=${postId}`;
    }
    let getProps = {
      url: url,
      successCallback: (data, message) => {
        setLoadingPostId(null);
        window.open(data.link, '_blank');
      },
      failureCallback: (message) => {
        setLoadingPostId(null);
        message && showToast('Error in getting post link', 'e');
      },
    };
    HttpServices.Get(getProps);
  };

  const handleAllPostChange = (e) => {
    const checked = e.target.checked;
    setAllPost(checked);
    setFormData((prev) => ({
      ...prev,
      allPost: checked,
      post_id: checked ? '' : prev.post_id,
    }));
  };

  const validateForm = () => {
    if (!formData.trigger_type) {
      showToast(translate('edit_trigger_dialog.trigger_type_required', 'Trigger type is required'), 'e');
      return false;
    }
    if (formData.is_dm_template) {
      if (!formData.dm_template?.title?.trim()) {
        showToast(translate('edit_trigger_dialog.template_title_required', 'Template title is required'), 'e');
        return false;
      }
      if (formData.dm_template?.buttons?.length > 3) {
        showToast(translate('edit_trigger_dialog.max_3_buttons', 'Maximum 3 buttons allowed in template'), 'e');
        return false;
      }
    } else {
      if (type === 'like' && !formData.dm_message.trim()) {
        showToast(translate('edit_trigger_dialog.dm_message_required_like', 'DM Message is required for like trigger'), 'e');
        return false;
      }
      if (type === 'comment' && !formData.dm_message.trim() && !formData.comment_reply.trim()) {
        showToast(translate('edit_trigger_dialog.dm_or_comment_required_comment', 'At least one of DM Message or Comment Reply is required for comment trigger'), 'e');
        return false;
      }
      if (type === 'message' && (!formData.dm_message.trim() || (!formData.any_trigger_value && formData.trigger_values.length === 0))) {
        showToast(translate('edit_trigger_dialog.trigger_values_and_dm_required_message', 'Trigger Values and DM Message are required for message trigger'), 'e');
        return false;
      }
    }
    if ((type === 'like' || type === 'comment') && !formData.allPost && !formData.post_id.trim()) {
      showToast(translate('edit_trigger_dialog.post_id_required', 'Post ID is required when not applying to all posts'), 'e');
      return false;
    }
    if (!formData.any_trigger_value && type !== 'like' && formData.trigger_values.length === 0 && !inputTriggerValue.trim()) {
      showToast(translate('edit_trigger_dialog.at_least_one_trigger_value', 'At least one trigger value is required unless any trigger value is enabled'), 'e');
      return false;
    }
    if (!formData.any_trigger_value && type !== 'like' && formData.trigger_values.length  === 0 && inputTriggerValue.trim()) {
      showToast(translate('edit_trigger_dialog.press_enter_to_add', 'Press Enter to add the trigger value to the list'), 'e');
      return;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    onSave(formData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{translate('edit_trigger_dialog.edit_automation_trigger', 'Edit Automation Trigger')}</DialogTitle>
      <DialogContent sx={{ padding: 3 }}>
        {trigger ? (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {(type === 'like' || type === 'comment') && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch checked={allPost} onChange={handleAllPostChange} color="primary" disabled={true} />
                    }
                    label={translate('edit_trigger_dialog.apply_to_all_posts', 'Apply to All Posts')}
                    sx={{ mb: 1 }}
                  />
                </Grid>
                {!allPost && (
                  <Grid item xs={12}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={10}>
                        <TextField
                          fullWidth
                          label={translate('edit_trigger_dialog.post_id', 'Post ID')}
                          name="post_id"
                          value={formData.post_id}
                          onChange={handleChange}
                          disabled={true}
                          variant="outlined"
                          size="small"
                          error={!formData.post_id.trim()}
                          helperText={!formData.post_id.trim() ? translate('edit_trigger_dialog.required', 'Required') : ''}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePostClick(formData.post_id, formData.id)}
                          disabled={!formData.post_id.trim() || loadingPostId === formData.id}
                          startIcon={
                            loadingPostId === formData.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <VisibilityIcon />
                            )
                          }
                          sx={{ height: '40px', minWidth: '100px' }}
                        >
                          {translate('edit_trigger_dialog.preview', 'Preview')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </>
            )}
            {type !== 'like' && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.any_trigger_value}
                      onChange={handleAnyTriggerValueToggle}
                      color="primary"
                    />
                  }
                  label={translate('edit_trigger_dialog.accept_any_trigger_value', 'Accept Any Trigger Value')}
                  sx={{ mb: 1 }}
                />
              </Grid>
            )}
            {type !== 'like' && !formData.any_trigger_value && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={translate('edit_trigger_dialog.trigger_values', 'Trigger Values')}
                  value={inputTriggerValue}
                  onChange={(e) => setInputTriggerValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTriggerValue()}
                  placeholder={translate('edit_trigger_dialog.enter_trigger_value', 'Enter trigger value and press Enter')}
                  variant="outlined"
                  size="small"
                  error={formData.trigger_values.length === 0}
                  helperText={formData.trigger_values.length === 0 ? translate('edit_trigger_dialog.at_least_one_trigger_value_required', 'At least one trigger value required') : ''}
                />
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.trigger_values.map((value) => (
                    <Chip
                      key={value}
                      label={value}
                      onDelete={() => handleDeleteTriggerValue(value)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={formData.is_dm_template} onChange={handleDmTemplateToggle} color="primary" />}
                label={translate('edit_trigger_dialog.use_generic_template_dm', 'Use Generic Template DM')}
                sx={{ mb: 1 }}
              />
            </Grid>
            {!formData.is_dm_template && (type === 'like' || type === 'comment' || type === 'message') && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={translate('edit_trigger_dialog.dm_message', 'DM Message')}
                  name="dm_message"
                  value={formData.dm_message}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  variant="outlined"
                  size="small"
                  error={(type === 'like' || type === 'message') && !formData.dm_message.trim()}
                  helperText={(type === 'like' || type === 'message') && !formData.dm_message.trim() ? translate('edit_trigger_dialog.required', 'Required') : ''}
                />
              </Grid>
            )}
            {formData.is_dm_template && (
              <Grid item xs={12}>
                <DMTemplateConfiguration
                  template={
                    formData.dm_template || {
                      title: '',
                      subtitle: '',
                      image_url: '',
                      default_action: { type: 'web_url', url: '' },
                      buttons: [],
                    }
                  }
                  onChange={handleTemplateChange}
                />
              </Grid>
            )}
            {type === 'comment' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={translate('edit_trigger_dialog.comment_reply', 'Comment Reply')}
                  name="comment_reply"
                  value={formData.comment_reply}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            )}
          </Grid>
        ) : (
          <Typography variant="body1" color="error">
            {translate('edit_trigger_dialog.no_trigger_data', 'No trigger data available')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <Button onClick={onClose} variant="outlined" color="error">
          {translate('edit_trigger_dialog.cancel', 'Cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!trigger} sx={{ ml: 1 }}>
          {translate('edit_trigger_dialog.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTriggerDialog;